import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { tenantAuditApi } from '@/api/tenant-audit';
import type { AuditLogRow } from '@/api/admin';
import { toast } from 'sonner';
import { btnOutlineNeon, btnPrimaryNeon } from '@/lib/admin-ui';
import { useTenantPermission } from '@/hooks/use-tenant-permission';

export function TenantAuditLogsPage() {
  const hasMemberView = useTenantPermission('tenant:user:view');
  const hasMemberManage = useTenantPermission('tenant:user:manage');
  const canAccessAudit = hasMemberView || hasMemberManage;

  const [rows, setRows] = useState<AuditLogRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const [actionFilter, setActionFilter] = useState('');
  const [appliedFilter, setAppliedFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!canAccessAudit) {
      setRows([]);
      setTotalPages(0);
      setTotal(0);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await tenantAuditApi.getAuditLogs({
        page,
        limit,
        action: appliedFilter.trim() || undefined,
      });
      setRows(res.data);
      setTotalPages(res.pagination.totalPages);
      setTotal(res.pagination.total);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })
          ?.response?.data?.message;
      const text = Array.isArray(msg) ? msg.join('; ') : msg;
      toast.error(
        typeof text === 'string' && text.length > 0
          ? text
          : '加载审计日志失败',
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, appliedFilter, canAccessAudit]);

  useEffect(() => {
    void load();
  }, [load]);

  function applyFilter() {
    setPage(1);
    setAppliedFilter(actionFilter);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">审计日志</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          本租户内成员与角色相关操作记录（仅本租户可见）；写入失败不影响主流程。
        </p>
      </div>

      {!canAccessAudit && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          当前角色无成员与审计查看权限（需
          <code className="mx-1 font-mono text-amber-200/90">tenant:user:view</code>
          或
          <code className="mx-1 font-mono text-amber-200/90">tenant:user:manage</code>
          ），下方列表为空。请联系租户管理员调整角色权限。
        </div>
      )}

      <Card className="border-white/10 bg-card/40">
        <CardHeader className="flex flex-row flex-wrap items-end justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="text-lg">操作记录</CardTitle>
            <CardDescription>
              共 {total} 条；按 action 子串筛选（不区分大小写）
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-2">
              <Label htmlFor="t-audit-action">action 包含</Label>
              <Input
                id="t-audit-action"
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                onKeyDown={(e) =>
                  e.key === 'Enter' && canAccessAudit && applyFilter()
                }
                placeholder="如 tenant_user"
                disabled={!canAccessAudit}
                className="w-[200px]"
              />
            </div>
            <Button
              type="button"
              className={btnPrimaryNeon}
              disabled={!canAccessAudit}
              onClick={applyFilter}
            >
              筛选
            </Button>
            <Button
              type="button"
              variant="outline"
              className={btnOutlineNeon}
              disabled={!canAccessAudit}
              onClick={() => {
                setActionFilter('');
                setAppliedFilter('');
                setPage(1);
              }}
            >
              清除
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">加载中…</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">时间</TableHead>
                    <TableHead>操作者</TableHead>
                    <TableHead>action</TableHead>
                    <TableHead>资源</TableHead>
                    <TableHead className="max-w-[280px]">metadata</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                        {r.createdAt.replace('T', ' ').slice(0, 19)}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="font-medium">{r.actorEmail}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {r.actorUserId}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-primary">{r.action}</TableCell>
                      <TableCell className="text-sm">
                        <span className="text-muted-foreground">{r.resourceType}</span>
                        {r.resourceId && (
                          <div className="mt-0.5 font-mono text-xs text-muted-foreground">
                            {r.resourceId}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[280px] break-all font-mono text-[11px] text-muted-foreground">
                        {r.metadata != null ? JSON.stringify(r.metadata) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {rows.length === 0 && (
                <p className="mt-4 text-sm text-muted-foreground">暂无记录</p>
              )}
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between gap-2">
                  <p className="text-sm text-muted-foreground">
                    第 {page} / {totalPages} 页
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={btnOutlineNeon}
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      上一页
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={btnOutlineNeon}
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      下一页
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
